import { apiFetchJson } from "./apiFetchRoute";
import type { LoginHistoryPage } from "../generated/types";
import type { EnumDeviceCategory, EnumLoginStatus } from "../generated/enums";

/** Taille de page par défaut. Le serveur plafonne à 200, quoi qu'on demande. */
export const LOGIN_HISTORY_PAGE_SIZE = 50;

/** Critères de l'onglet « Connexions utilisateurs ». Un champ vide = filtre inactif. */
export type LoginHistoryFilters = {
  /**
   * Recherche GLOBALE, faite en SQL côté serveur sur toutes les colonnes textuelles de
   * la ligne (un bloc par mot, tous les mots requis). Pas en mémoire : l'historique est
   * paginé côté serveur, une recherche locale ne verrait que la page affichée.
   */
  q?: string;
  /** Recherche PARTIELLE et insensible à la casse (« dupont » trouve tout un compte). */
  email?: string;
  status?: EnumLoginStatus | "";
  /** Égalité exacte, contrairement à l'email : c'est ce qui rend l'index utile. */
  ip?: string;
  deviceType?: EnumDeviceCategory | "";
  /** Compte : par ID, l'email pouvant changer alors que l'audit reste sur le compte. */
  idAccount?: number;
  /** Libellés normalisés issus du User-Agent (« Chrome », « Windows ») : égalité stricte. */
  browser?: string;
  os?: string;
  dateFrom?: string; // AAAA-MM-JJ
  dateTo?: string; // AAAA-MM-JJ
};

/**
 * Récupère une page de l'historique des connexions utilisateurs (admin/PSC).
 *
 * Pagination SERVEUR : la table atteint ~180 000 lignes à 6 mois de rétention.
 * `total` est le nombre de lignes correspondant aux filtres sans la pagination — il sert
 * à afficher « 1–50 sur 1234 » et à savoir s'il reste une page.
 *
 * `?? { total: 0, rows: [] }` : la route répond **204 No Content** quand il n'y a aucune
 * ligne, et `apiFetchJson` renvoie alors `undefined`. Sans ce repli, lire `.rows`
 * lèverait un TypeError — bug déjà rencontré sur `fetchDevices`.
 */
export const fetchLoginHistory = async (
  filters: LoginHistoryFilters = {},
  page = 0,
  pageSize = LOGIN_HISTORY_PAGE_SIZE
): Promise<{ page: LoginHistoryPage; error: string | null }> => {
  try {
    const result = (await apiFetchJson("getAdminLoginhistory", undefined, {
      query: {
        q: filters.q?.trim() || undefined,
        email: filters.email?.trim() || undefined,
        status: filters.status || undefined,
        ip: filters.ip?.trim() || undefined,
        device_type: filters.deviceType || undefined,
        id_account: filters.idAccount || undefined,
        browser: filters.browser?.trim() || undefined,
        os: filters.os?.trim() || undefined,
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
        limit: pageSize,
        offset: page * pageSize,
      },
    })) as LoginHistoryPage | undefined;

    return { page: result ?? { total: 0, rows: [] }, error: null };
  } catch (e) {
    return {
      page: { total: 0, rows: [] },
      error: "login_history_load_error",
    };
  }
};
